export type CampaignType = 'Promotion' | 'Brand' | 'Theme';
export type TaskStatus = 'Not started' | 'In progress' | 'Done';
export type BrandPriority = 'A' | 'B' | 'C';
/** Pricing stance toward the brand — drives the discount-policy warnings. */
export type DiscountStance = 'open' | 'negotiate' | 'restricted';

export interface Brand {
  id: string;
  name: string;
  /** Short code, e.g. "DK". Read-derived from Dory's design country when
   *  `country_source` is 'dory'; mosy's own value otherwise. */
  country: string;
  /** Full name, e.g. "Denmark" (from Dory, or mapped from the code). */
  country_name: string;
  country_source: 'dory' | 'mosy';
  priority: BrandPriority;
  discount_stance: DiscountStance;
  /** Year-round shop discount we run on the brand's products, e.g.
   *  "10%" or "15% on lighting only". Empty when the brand has no
   *  permanent discount. Surfaced as the auto-prefilled `discount`
   *  on new partnership tracking rows. */
  permanent_discount: string;
  /** Average gross margin at RRP across the active catalog, e.g. "45%".
   *  Auto-computed weekly (read-only). Empty when no cost data. */
  avg_margin: string;
  color: string;
  accent: string;
  logo: string | null;
  has_visibility_plan: boolean;
  /** The brand's exact vendor name in the PIM catalog, used when creating
   *  Pricy campaigns from mosy. Empty = fall back to the mosy brand name. */
  pim_vendor: string;
  /** Brand identity card — shared with Dory (same database, same row).
   *  Editing these in either tool changes both. */
  website_url: string;
  /** Media bank / Presscloud / download database for brand assets. */
  media_bank_url: string;
  /** "How to get in": login, who to ask, quirks. Free text. */
  media_bank_access: string;
  /** Agreed purchase terms, live from Dory (read-only), e.g.
   *  "60% of list price · 40% supplier discount". Empty when Dory has none. */
  cost_rule: string;
  /** The one margin figure mosy shows, e.g. "45%": Dory's agreed supplier
   *  discount when the brand has a cost rule there, else `avg_margin`. */
  margin: string;
  margin_source: 'dory' | 'catalog';
  /** Linked Odoo supplier (res.partner id) — bound once from the
   *  partnership billing tab so invoices reuse it. Null = not linked. */
  odoo_partner_id: number | null;
  odoo_partner_name: string;
  contact_name: string;
  contact_role: string;
  contact_email: string;
  contact_phone: string;
  /** Deprecated — kept for backwards compat, no longer edited in the UI. */
  contact_notes: string;
  contact_name_2: string;
  contact_role_2: string;
  contact_email_2: string;
  contact_phone_2: string;
  contact_name_3: string;
  contact_role_3: string;
  contact_email_3: string;
  contact_phone_3: string;
  notes: string;
}

export interface CampaignSummary {
  id: string;
  name: string;
  emoji: string;
  type: CampaignType;
  /** Null when the campaign is in the "To plan" group (no dates picked yet). */
  start_date: string | null;
  end_date: string | null;
  owner: number | null;
  description: string;
  notes: string;
  parent: string | null;
  brands: string[];
}

export interface CampaignShareLink {
  brand: string | null;
  token: string;
  enabled: boolean;
  /** Text at the top of the shared page; empty = the campaign description. */
  intro: string;
}

export interface Campaign extends CampaignSummary {
  brand_details?: Brand[];
  owner_details?: MosyUser | null;
  /** Partner share links, one per brand (brand null = whole campaign);
   *  managed via the share/unshare actions. */
  shares?: CampaignShareLink[];
  /** Set once the campaign has been created in Pricy (PIM pricing module). */
  pricy_campaign_id?: number | null;
  pricy_synced_at?: string | null;
  pricy_campaign_url?: string | null;
}

export interface Task {
  id: string;
  name: string;
  due_date: string;
  status: TaskStatus;
  campaign: string | null;
  asset: number | null;
  asset_title: string | null;
  asset_category: AssetCategory | null;
  /** Go-live date of the linked placement, so task lists can show how
   *  close the publication is without opening the campaign. */
  asset_publish_date: string | null;
  owner: number | null;
  created_by: number | null;
  assignees: number[];
  assignee_details?: MosyUser[];
}

export type AssetCategory = 'Email' | 'Social Media' | 'Homepage' | 'Catalog' | 'Promotion';

export type AssetType =
  | 'Newsletter'
  | 'Instagram Post' | 'Instagram Story' | 'Instagram Reel' | 'Instagram Giveaway' | 'Facebook Post'
  | 'Hero Banner 100%' | 'Hero Banner 50%' | 'Homepage Product Grid' | 'Homepage Collection Cards' | 'Homepage Promotional Banner' | 'Homepage Product Carousel' | 'Homepage Browse by Room' | 'Homepage Brand Logo Carousel' | 'Pop-ups & Banners' | 'Navigation Bar'
  | 'Banner on PDP' | 'Product Label' | 'Collection Page' | 'Featured products on collection'
  | 'Discount';

export type AssetStatus = 'Not started' | 'In progress' | 'Ready' | 'Done';

export const ASSET_CATEGORIES: AssetCategory[] = [
  'Email', 'Social Media', 'Homepage', 'Catalog', 'Promotion',
];

export const ASSET_TYPES_BY_CATEGORY: Record<AssetCategory, AssetType[]> = {
  Email: ['Newsletter'],
  'Social Media': ['Instagram Post', 'Instagram Story', 'Instagram Reel', 'Instagram Giveaway', 'Facebook Post'],
  Homepage: ['Hero Banner 100%', 'Hero Banner 50%', 'Homepage Product Grid', 'Homepage Collection Cards', 'Homepage Promotional Banner', 'Homepage Product Carousel', 'Homepage Browse by Room', 'Homepage Brand Logo Carousel', 'Pop-ups & Banners', 'Navigation Bar'],
  Catalog: ['Banner on PDP', 'Product Label', 'Collection Page', 'Featured products on collection'],
  Promotion: ['Discount'],
};

export const ASSET_STATUSES: AssetStatus[] = ['Not started', 'In progress', 'Ready', 'Done'];

export const TASK_STATUSES: TaskStatus[] = ['Not started', 'In progress', 'Done'];

export const NEWSLETTER_THEMES = [
  'Editorial', 'Product drop', 'Gift guide', 'Restock', 'Seasonal', 'Trade', 'Clearance',
];
export const NEWSLETTER_SEGMENTS = [
  'All subscribers', 'VIP', 'Trade', 'Brand lovers — IT', 'Brand lovers — DK', 'Lapsed',
];

export interface CampaignAsset {
  id: number;
  title: string;
  category: AssetCategory;
  asset_type: AssetType;
  campaign: string | null;
  campaign_name: string | null;
  campaign_brands: Brand[];
  /** Direct brand link for assets that aren't part of a campaign (or that
   *  should also surface under specific brands beyond the campaign). */
  brands: string[];
  brand_details: Brand[];
  /** Optional. When set on an Email asset, that newsletter counts as
   *  'Dedicated newsletter' for the brand under its visibility plan. */
  dedicated_brand: string | null;
  /** Null when the asset is in the "To plan" tray (no date picked yet). */
  publish_date: string | null;
  end_date: string | null;
  publish_time: string | null;
  /** Sellable placement: number of brand spots. Null = normal placement.
   *  Taken = brands tagged on the placement. */
  brand_spots: number | null;
  /** Slot placements: tagged brands in booking order (read-only). */
  booking_order?: BookingOrderEntry[];
  /** Price of one brand spot when it differs from the campaign's price for the type; decimal string, null = use the type's price. */
  slot_price: string | null;
  /** Stand-out slot on the booking page. */
  highlighted: boolean;
  /** Placements & prices line the slots are sold as (id), and its name. */
  sold_as: number | null;
  sold_as_label: string | null;
  status: AssetStatus;
  description: string;
  caption: string;
  theme: string;
  segment: string;
  /** Live URL of the published asset (e.g. Instagram permalink). */
  proof_url: string;
  /** Base64 PNG screenshot of the published asset. */
  proof_screenshot: string;
  /** Further screenshots after the first one (base64 data-URLs). */
  proof_extra_screenshots?: string[];
  /** Post-publish metrics. Schema depends on asset category — see
   *  the Results tab in the partnership editor for the v1 fields:
   *  newsletter → recipients/opens/clicks; Instagram → reach/impressions/engagement. */
  metrics: Record<string, number | string | null>;
  /** "Banner on PDP" only: state of its shop banner (null for other types). */
  shop_banner?: ShopBannerSummary | null;
  created_at: string;
  updated_at: string;
}

export interface Notification {
  id: number;
  kind: string;
  title: string;
  body: string;
  link_url: string;
  read_at: string | null;
  created_at: string;
}

export interface MosyUser {
  user_id: number;
  username: string;
  first_name: string;
  last_name: string;
  email: string;
  avatar: string | null;
  display_name: string;
  initials: string;
  color: string;
}

export interface Attachment {
  id: number;
  name: string;
  content_type: string;
  size_bytes: number;
  campaign: string | null;
  brand: string | null;
  task: string | null;
  bf_terms: number | null;
  uploaded_by: number | null;
  uploaded_by_details: {
    user_id: number;
    username: string;
    first_name: string;
    last_name: string;
    email: string;
  } | null;
  uploaded_at: string;
}

export const MAX_ATTACHMENT_SIZE_BYTES = 25 * 1024 * 1024;

export type ShopBannerLiveState = 'Draft' | 'Scheduled' | 'Live now' | 'Ended';

export interface ShopBannerSummary {
  exists: boolean;
  live_state: ShopBannerLiveState;
  /** null = never published. */
  in_sync: boolean | null;
  headline: string;
}

/** One brand on a slot placement, in booking order; signed_up_at null =
 *  tagged by hand (not booked on the booking page). */
export interface BookingOrderEntry {
  brand_id: string;
  name: string;
  signed_up_at: string | null;
}
